import { useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { OcaDynamicValuesForm } from '../types';
import { ocaDynamicValuesSchema } from '../model/schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import { CurrencyInputField, InterestInputField } from 'components/form';
import { VendorProfileOcaAutofillSettings } from '@trnsact/trnsact-shared-types';
import { formatCurrency } from 'utils';
import { Nullable } from 'global';

interface Props {
  ocaAutofillSettings?: Nullable<VendorProfileOcaAutofillSettings>;
  onChange: (value: VendorProfileOcaAutofillSettings) => void;
}

export const OcaAutofillValues = ({ ocaAutofillSettings, onChange }: Props) => {
  const form = useForm<OcaDynamicValuesForm>({
    resolver: zodResolver(ocaDynamicValuesSchema),
    defaultValues: {
      initialApr: (ocaAutofillSettings?.autofillValues?.initialApr?.value as number) ?? '',
      estimatedFinanceCharge: (ocaAutofillSettings?.autofillValues?.estimatedFinanceCharge?.value as number) ?? '',
      estimatedTotalPayments: (ocaAutofillSettings?.autofillValues?.estimatedTotalPayments?.value as number) ?? '',
    },
  });

  const handleInputChange = async (fieldName: string, value: number, label: string) => {
    const vpOcaAutofillSettingsToSet = {
      ...ocaAutofillSettings,
      autofillValues: {
        ...ocaAutofillSettings?.autofillValues,
        [fieldName]: {
          value,
          label,
        },
      },
    };
    onChange(vpOcaAutofillSettingsToSet);
  };

  return (
    <Box>
      <Typography variant="h6" component="span">
        Interest Rate Disclosure
      </Typography>
      <Box mt={1}>
        <Stack gap="1rem" direction="row">
          <InterestInputField
            control={form.control}
            label="Initial Annual Percentage Rate (APR)"
            name="initialApr"
            textFieldProps={{ variant: 'outlined', size: 'small' }}
            customOnChange={(nextValue, name) => {
              form.setValue(name, nextValue);
              handleInputChange(name, nextValue, `${nextValue}%`);
            }}
          />
          <CurrencyInputField
            control={form.control}
            name="estimatedFinanceCharge"
            label="Estimated Finance Charge"
            textFieldProps={{ variant: 'outlined' }}
            customOnChange={(nextValue, name) => {
              form.setValue(name, nextValue);
              handleInputChange(name, nextValue, formatCurrency(nextValue));
            }}
          />
          <CurrencyInputField
            control={form.control}
            name="estimatedTotalPayments"
            label="Estimated Total Payments"
            textFieldProps={{ variant: 'outlined' }}
            customOnChange={(nextValue, name) => {
              form.setValue(name, nextValue);
              handleInputChange(name, nextValue, formatCurrency(nextValue));
            }}
          />
        </Stack>
      </Box>
    </Box>
  );
};
